/**
 * DemoDataBadge — floating super-admin only seeder
 * Bottom-right corner, toggles between "Seed Demo Data" and "Clear Demo Data".
 * State persisted in localStorage under `rudrik_demo_mode`.
 * Calls POST /api/demo/seed   /  DELETE /api/demo/clear   (JWT auth).
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Database, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/demo`;
const LS_KEY = "rudrik_demo_mode";

function readLS() {
  try { return localStorage.getItem(LS_KEY) === "on"; } catch { return false; }
}
function writeLS(on) {
  try { localStorage.setItem(LS_KEY, on ? "on" : "off"); } catch { /* ignore */ }
}

export function DemoDataBadge() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seeded, setSeeded] = useState(readLS());
  const [counts, setCounts] = useState(null);

  const role = String(user?.role || "").toLowerCase();
  const isSuperAdmin = role === "super_admin" || role === "admin";

  const refreshStatus = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const { data } = await axios.get(`${API}/status`, { withCredentials: true });
      setCounts(data);
      const isSeeded = !!data?.demo_seeded;
      setSeeded(isSeeded);
      writeLS(isSeeded);
    } catch (e) { /* silent — backend down or no auth */ }
  }, [isSuperAdmin]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  if (!isSuperAdmin) return null;

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/seed`, {}, { withCredentials: true });
      toast.success(
        `Seeded ${data.customers} customers · ${data.screenings} screenings · ${data.cases} cases`,
      );
      writeLS(true);
      setSeeded(true);
      await refreshStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear ALL Rudrik demo data from this tenant?")) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/clear`, { withCredentials: true });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${total} demo records`);
      writeLS(false);
      setSeeded(false);
      await refreshStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  const baseBtn = {
    background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
    color: "#f1f5f9",
    border: "1px solid #3b82f6",
    borderRadius: "999px",
    padding: "10px 16px",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    cursor: busy ? "wait" : "pointer",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    display: "flex", alignItems: "center", gap: "8px",
  };

  return (
    <div
      data-testid="demo-data-badge"
      style={{
        position: "fixed",
        bottom: "22px",
        right: "22px",
        zIndex: 9999,
        display: "flex", flexDirection: "column", alignItems: "flex-end",
        gap: "10px",
      }}
    >
      {open && (
        <div
          data-testid="demo-data-panel"
          style={{
            background: "#0d1117",
            border: "1px solid #1e2530",
            borderRadius: "14px",
            padding: "16px 18px",
            minWidth: "260px",
            boxShadow: "0 14px 38px rgba(2, 6, 23, 0.6)",
            color: "#cbd5e1",
            fontSize: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <Sparkles style={{ width: 14, height: 14, color: "#3b82f6" }} />
            <strong style={{ color: "#f1f5f9", fontSize: "12px" }}>Rudrik Demo Data</strong>
          </div>
          <div style={{ color: "#94a3b8", lineHeight: 1.5, marginBottom: "10px" }}>
            Seeds 500 customers, 50 screenings, 3 STRs, 5 adverse media articles,
            3 open cases — all tagged <code style={{ color: "#3b82f6" }}>RUDRIK_DEMO_V1</code>.
          </div>
          {counts && (
            <div style={{ fontFamily: "ui-monospace, monospace", fontSize: "11px",
                          color: "#64748b", marginBottom: "10px", lineHeight: 1.6 }}>
              <div data-testid="demo-counts-customers">customers: {counts.customers}</div>
              <div data-testid="demo-counts-screenings">screenings: {counts.screening_records}</div>
              <div data-testid="demo-counts-cases">cases: {counts.cases}</div>
              <div data-testid="demo-counts-strs">STRs: {counts.str_reports}</div>
              <div data-testid="demo-counts-adverse">adverse media: {counts.adverse_media}</div>
            </div>
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              data-testid="demo-seed-btn"
              onClick={seed}
              disabled={busy}
              style={{ ...baseBtn, padding: "8px 12px", flex: 1 }}
            >
              {busy ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> :
                       <Database style={{ width: 12, height: 12 }} />}
              Seed
            </button>
            <button
              data-testid="demo-clear-btn"
              onClick={clear}
              disabled={busy || !seeded}
              style={{
                ...baseBtn,
                background: seeded ? "linear-gradient(135deg, #b91c1c, #ef4444)" : "#1e2530",
                border: seeded ? "1px solid #ef4444" : "1px solid #1e2530",
                color: seeded ? "#fff" : "#475569",
                padding: "8px 12px", flex: 1,
              }}
            >
              <Trash2 style={{ width: 12, height: 12 }} /> Clear
            </button>
          </div>
        </div>
      )}

      <button
        data-testid="demo-data-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          ...baseBtn,
          background: seeded
            ? "linear-gradient(135deg, #15803d, #22c55e)"
            : "linear-gradient(135deg, #1d4ed8, #2563eb)",
          border: seeded ? "1px solid #22c55e" : "1px solid #3b82f6",
        }}
      >
        <Database style={{ width: 14, height: 14 }} />
        {seeded ? "Demo Data ON" : "Demo Data"}
      </button>
    </div>
  );
}

export default DemoDataBadge;
